/* eslint-disable max-lines -- Pre-existing violation */
import {skipToken} from '@reduxjs/toolkit/query';
import {isBefore, startOfToday} from 'date-fns';
import {
  Alert,
  Button,
  ButtonGroup,
  closeCurrentDialog,
  closeDialog,
  DataStatus,
  DialogFooter,
  EmptyStatus,
  Form,
  FormButton,
  FormField,
  getHoursAndMinutesFromTime,
  HelperText,
  Label,
  openConfirmDialog,
  openDialog,
  Separator,
  TimeType,
} from 'platform/components';
import {
  Box,
  Heading,
  HStack,
  VStack,
  Text,
  Space,
  Show,
  Grid,
  GridItem,
  Stack,
} from 'platform/foundation';
import {useDateTimeFormatter} from 'platform/locale';
import {match} from 'ts-pattern';

import {useState} from 'react';
import {UseFormReturn} from 'react-hook-form';

import {isNil} from 'ramda';
import {isNilOrEmpty, isNotNil} from 'ramda-adjunct';

import {
  CustomerResponseBodyV2,
  useGetCurrentUserInfoQuery,
  useGetCustomerV2Query,
  useGetUserQuery,
  useGetUsersQuery,
} from '@dms/api/customer';
import {
  TestDriveResponseBody,
  useDeleteTestDriveMutation,
  useGetTestDriveQuery,
  useGetVehicleMileageQuery,
  useGetVehicleV2Query,
  useLazyGetVehicleMileageQuery,
} from '@dms/api/sales';
import {UserStateFilterSchema} from '@dms/api/shared';
import {SaleVehicleSellingState, useGetSaleVehicleQuery} from '@dms/api/vehicle';
import i18n from '@dms/i18n';
import {
  BATTERY_CHARGE_MAX,
  BATTERY_CHARGE_MIN,
  BATTERY_CHARGE_STEP,
  CustomerMatchOrCreate,
  DeletedUserAlertField,
  getCustomerName,
  getDriversLicenceNumber,
  getLevelIndicatorFlags,
  getNaturalPersonFullName,
  levelIndicatorsFor,
  MileageInput,
  handleApiError,
  mapUsersToSelectOptions,
  moveToHeadById,
  PredefinedNotes,
  useUserAbsenceIndicators,
  useUsersWithPersistedAssignee,
  VehicleWidget,
} from '@dms/shared';

import {isNotNilOrEmpty, parseDate, getApiDateString, suffixTestId, isNilOrZero} from 'shared/web';

import {TEST_DRIVE_DIALOG_ID} from '../../constants/TEST_DRIVE_DIALOG_ID';
import {useTestDriveMileageSubmit} from './hooks/useTestDriveMileageSubmit';
import {TestDriveCalendarVehicleDialog} from './TestDriveCalendarVehicleDialog';
import {
  TestDriveCreateDialogSchema,
  TestDriveDialogSchema,
  TestDriveDialogSchemaType,
} from './TestDriveDialogSchema';
import {TestDriveSelectVehicleDialog} from './TestDriveSelectVehicleDialog';
import {TestDriveStateSwitcher} from './TestDriveStateSwitcher';

interface TestDriveDialogProps {
  testDriveId?: string;
  testDrive?: TestDriveResponseBody;
  /**
   * Omit to open in create mode: the customer is picked in the dialog and the business case is
   * resolved — reused or created in `branchId` — only when the drive is saved.
   */
  businessCaseId?: string;
  vehicleId?: string;
  branchId?: string;
  customerId?: string;
  isReadOnly?: boolean;
  isDeleteHidden?: boolean;
  isVehicleChangeHidden?: boolean;
  defaultSlot?: {start: Date; end: Date | null; isTimeIncluded?: boolean};
  isCalendarVehiclePicker?: boolean;
}

/** The test drive stores the fuel tank level as a 0-1 ratio, not as a percent. */
const FUEL_TANK_RATIO_MIN = 0;
const FUEL_TANK_RATIO_MAX = 1;
const SELECT_VEHICLE_DIALOG_ID = 'TEST_DRIVE_SELECT_VEHICLE_DIALOG';
const SELECT_CUSTOMER_DIALOG_ID = 'TEST_DRIVE_SELECT_CUSTOMER_DIALOG';

/** A new test drive may only be booked on a vehicle still on the lot for sale. */
const ELIGIBLE_VEHICLE_SELLING_STATES: SaleVehicleSellingState[] = ['for-sale', 'selling'];

const isReturnedAtInPast = (
  returnedAtDate: string | null | undefined,
  returnedAtTime: string | null | undefined
) => {
  if (!returnedAtDate || !returnedAtTime) {
    return false;
  }

  const parsedDate = parseDate(returnedAtDate);
  const parsedTime = getHoursAndMinutesFromTime(returnedAtTime);

  if (!parsedTime || !parsedDate) {
    return false;
  }

  const {hour, minute} = parsedTime;
  parsedDate.setHours(Number(hour)); // eslint-disable-line eag/no-native-date-math -- Pre-existing native Date math, migrate to date-fns when possible
  parsedDate.setMinutes(Number(minute)); // eslint-disable-line eag/no-native-date-math -- Pre-existing native Date math, migrate to date-fns when possible

  const currentDateTime = new Date();

  return isBefore(parsedDate, currentDateTime);
};

const MILEAGE_FIELDS = ['mileageBefore', 'mileageAfter'] as const;

// eslint-disable-next-line complexity, max-lines-per-function, max-statements -- Pre-existing violation
export function TestDriveDialog(props: TestDriveDialogProps) {
  const formatTime = useDateTimeFormatter();
  const [deleteTestDrive] = useDeleteTestDriveMutation();
  const [pickedVehicleId, setPickedVehicleId] = useState<string | null>(null);
  const [pickedBranchId, setPickedBranchId] = useState<string | null>(null);
  const [pickedCustomerId, setPickedCustomerId] = useState<string | null>(null);

  const {
    data: fetchedTestDrive,
    isLoading: isTestDriveLoading,
    isError: isTestDriveError,
  } = useGetTestDriveQuery(isNotNil(props.testDriveId) ? {id: props.testDriveId} : skipToken);

  const testDrive = props.testDrive ?? fetchedTestDrive;
  const customerId = props.customerId ?? testDrive?.customerId ?? pickedCustomerId ?? undefined;
  const selectedVehicleId = pickedVehicleId ?? props.vehicleId ?? testDrive?.vehicleId ?? null;
  const selectedBranchId = pickedBranchId ?? props.branchId ?? null;
  const isCreateMode = isNil(props.businessCaseId);

  const {data: currentUser} = useGetCurrentUserInfoQuery();
  const {data: users, isLoading: areUsersLoading} = useGetUsersQuery({
    stateFilter: UserStateFilterSchema.enum.active,
  });
  const {data: userData} = useGetUserQuery(
    isNotNilOrEmpty(testDrive?.createdBy) ? {id: testDrive.createdBy} : skipToken
  );

  const {
    data: vehicleData,
    currentData: currentVehicleData,
    isFetching: isVehicleFetching,
    isError: isVehicleError,
  } = useGetVehicleV2Query(
    isNotNilOrEmpty(selectedVehicleId) ? {vehicleId: selectedVehicleId} : skipToken
  );

  const {
    data: vehicleMileage,
    currentData: currentVehicleMileage,
    isLoading: isVehicleMileageLoading,
    isFetching: isVehicleMileageFetching,
  } = useGetVehicleMileageQuery(
    isNotNilOrEmpty(selectedVehicleId) ? {vehicleId: selectedVehicleId} : skipToken
  );
  const [loadVehicleMileage] = useLazyGetVehicleMileageQuery();

  // `defaultValues` is read once at mount, so a preselected vehicle's mileage has to be there
  // already. A vehicle picked in the dialog fills the fields in `fillMileageFromVehicle` instead.
  const isPreselectedMileageLoading =
    isVehicleMileageLoading && selectedVehicleId === props.vehicleId;

  const {
    data: saleVehicle,
    isFetching: isSaleVehicleFetching,
    isError: isSaleVehicleError,
  } = useGetSaleVehicleQuery(
    isNotNilOrEmpty(selectedVehicleId) ? {vehicleId: selectedVehicleId} : skipToken
  );

  const {data: customer} = useGetCustomerV2Query(isNotNil(customerId) ? {customerId} : skipToken);

  const customerDriversLicenceNumber = getDriversLicenceNumber(customer);

  // `currentData` is undefined while the selected vehicle is (re)loading, unlike `data`, which
  // keeps the previous vehicle. The level sliders stay hidden and saving stays blocked until it
  // resolves, so the payload is never built from the fallback indicators of a stale or missing vehicle.
  // A failed fetch also counts as resolved, so an unreachable vehicle leaves the dialog usable with
  // the fuel-only fallback instead of locking the sliders and Save forever.
  const isVehicleResolved =
    isNilOrEmpty(selectedVehicleId) || isNotNil(currentVehicleData) || isVehicleError;

  // Only a new test drive is gated on the vehicle's sale status — an already-scheduled drive
  // keeps being editable even if the vehicle sold in the meantime.
  const isVehicleBlacklistedForTestDrive =
    isCreateMode &&
    isNotNilOrEmpty(selectedVehicleId) &&
    !isSaleVehicleFetching &&
    !isSaleVehicleError &&
    (isNil(saleVehicle?.sellingState) ||
      !ELIGIBLE_VEHICLE_SELLING_STATES.includes(saleVehicle.sellingState));
  const levelIndicators = levelIndicatorsFor(currentVehicleData?.fuelType);
  const {hasFuel, hasBattery} = getLevelIndicatorFlags(levelIndicators);

  const {handleSubmit, isSubmitting} = useTestDriveMileageSubmit({
    businessCaseId: props.businessCaseId,
    customerId,
    selectedVehicleId,
    selectedBranchId,
    levelIndicators,
    testDrive,
    vehicleMileage: currentVehicleMileage?.mileage,
  });

  const usersWithDriver = useUsersWithPersistedAssignee(users, testDrive?.userId);
  const {getStatusIndicator} = useUserAbsenceIndicators();
  const usersOptions = mapUsersToSelectOptions(
    moveToHeadById(currentUser?.id ?? '', usersWithDriver),
    getStatusIndicator
  );

  const scheduledAt =
    (testDrive?.scheduledAt ? parseDate(testDrive.scheduledAt) : props.defaultSlot?.start) ??
    new Date();

  const returnedAt =
    (testDrive?.returnedAt ? parseDate(testDrive.returnedAt) : props.defaultSlot?.end) ?? null;

  const isScheduledAtTimeKnown =
    isNotNilOrEmpty(testDrive?.scheduledAt) || (props.defaultSlot?.isTimeIncluded ?? true);

  const defaultValues = {
    state: testDrive?.state ?? ('planned' as const),
    userId: testDrive?.userId ?? currentUser?.id ?? '',
    fuelBefore: (testDrive?.fuelBefore ?? 0) / 100,
    fuelAfter: (testDrive?.fuelAfter ?? 0) / 100,
    batteryChargeBefore: testDrive?.batteryChargeBefore ?? null,
    batteryChargeAfter: testDrive?.batteryChargeAfter ?? null,
    mileageBefore: testDrive?.mileageBefore ?? vehicleMileage?.mileage ?? 0,
    mileageAfter: testDrive?.mileageAfter ?? vehicleMileage?.mileage ?? 0,
    note: testDrive?.note ?? '',
    internalNote: testDrive?.internalNote ?? '',
    customerFeedback: testDrive?.customerFeedback ?? '',
    vehicleId: selectedVehicleId ?? null,
    scheduledAtDate: getApiDateString(scheduledAt),
    scheduledAtTime: isScheduledAtTimeKnown ? formatTime('timeShort', scheduledAt) : undefined,
    returnedAtDate: isNotNil(returnedAt) ? getApiDateString(returnedAt) : null,
    returnedAtTime: isNotNil(returnedAt) ? formatTime('timeShort', returnedAt) : null,
    customerId: customerId ?? null,
    customerName: testDrive?.customerName ?? getCustomerName(customer) ?? null,
    customerDriversLicence:
      testDrive?.customerDriversLicence ?? customerDriversLicenceNumber ?? null,
  };

  const fillMileageFromVehicle = (
    vehicleId: string,
    formApi: UseFormReturn<TestDriveDialogSchemaType>
  ) =>
    loadVehicleMileage({vehicleId}, true).then((result) => {
      if (formApi.getValues('vehicleId') !== vehicleId) {
        return;
      }
      const isNoMileageHistory =
        isNotNil(result.error) && 'status' in result.error && result.error.status === 404;
      if (result.isError && !isNoMileageHistory) {
        return;
      }
      const mileage = result.data?.mileage ?? 0;
      MILEAGE_FIELDS.filter((field) => !formApi.getFieldState(field).isDirty).forEach((field) =>
        formApi.setValue(field, mileage)
      );
    });

  const handleSelectVehicleCallback = (
    data: {vehicleId: string; start: Date; end: Date},
    formApi: UseFormReturn<TestDriveDialogSchemaType>
  ) => {
    const isVehicleChanged = formApi.getValues('vehicleId') !== data.vehicleId;
    setPickedVehicleId(data.vehicleId);
    formApi.setValue('vehicleId', data.vehicleId, {shouldValidate: formApi.formState.isSubmitted});
    if (isVehicleChanged) {
      void fillMileageFromVehicle(data.vehicleId, formApi);
    }
    formApi.setValue('scheduledAtDate', getApiDateString(data.start));
    formApi.setValue('scheduledAtTime', data.start.toTimeString().split(' ')[0].substring(0, 5));
    formApi.setValue('returnedAtDate', getApiDateString(data.end));
    formApi.setValue('returnedAtTime', data.end.toTimeString().split(' ')[0].substring(0, 5));
    closeDialog(SELECT_VEHICLE_DIALOG_ID);
  };

  const handleSelectVehicle = (formApi: UseFormReturn<TestDriveDialogSchemaType>) => {
    if (props.isCalendarVehiclePicker === true) {
      openDialog(
        <TestDriveCalendarVehicleDialog
          onVehicleSelect={(vehicle) => {
            const isVehicleChanged = formApi.getValues('vehicleId') !== vehicle.vehicleId;
            setPickedVehicleId(vehicle.vehicleId);
            setPickedBranchId(vehicle.branchId);
            formApi.setValue('vehicleId', vehicle.vehicleId, {
              shouldValidate: formApi.formState.isSubmitted,
            });
            if (isVehicleChanged) {
              void fillMileageFromVehicle(vehicle.vehicleId, formApi);
            }
            closeDialog(SELECT_VEHICLE_DIALOG_ID);
          }}
          data-testid="testDriveDialog-calendarVehiclePicker"
        />,
        {
          title: i18n.t('entity.testDrive.labels.selectVehicle'),
          size: 'small',
          id: SELECT_VEHICLE_DIALOG_ID,
        }
      );
      return;
    }

    openDialog(
      <TestDriveSelectVehicleDialog
        onTestDriveSelect={(data) => handleSelectVehicleCallback(data, formApi)}
      />,
      {
        title: i18n.t('entity.testDrive.labels.selectVehicle'),
        size: 'large',
        id: SELECT_VEHICLE_DIALOG_ID,
      }
    );
  };

  const handleOpenCalendar = (formApi: UseFormReturn<TestDriveDialogSchemaType>) => {
    openDialog(
      <TestDriveSelectVehicleDialog
        onTestDriveSelect={(data) => handleSelectVehicleCallback(data, formApi)}
        defaultVehicle={currentVehicleData ?? null}
        defaultActiveTab="calendar"
      />,
      {
        title: i18n.t('entity.testDrive.labels.selectVehicle'),
        size: 'large',
        id: SELECT_VEHICLE_DIALOG_ID,
      }
    );
  };

  const handleSelectCustomer = (formApi: UseFormReturn<TestDriveDialogSchemaType>) => {
    const handlePickedCustomer = (pickedCustomer: CustomerResponseBodyV2) => {
      setPickedCustomerId(pickedCustomer.id);
      formApi.setValue('customerId', pickedCustomer.id, {
        shouldValidate: formApi.formState.isSubmitted,
      });
      /*
       * `defaultValues` is evaluated once at mount, so the driver fields have to be filled here —
       * they stay editable, since the driver is not always the customer.
       */
      formApi.setValue('customerName', getCustomerName(pickedCustomer) ?? null);
      formApi.setValue('customerDriversLicence', getDriversLicenceNumber(pickedCustomer) ?? null);
      closeDialog(SELECT_CUSTOMER_DIALOG_ID);
    };

    openDialog(
      <CustomerMatchOrCreate
        onCustomer={handlePickedCustomer}
        vehicleId={selectedVehicleId}
        data-testid="testDriveDialog-customerPicker"
      />,
      {
        title: i18n.t('entity.customer.labels.customer'),
        size: 'large',
        id: SELECT_CUSTOMER_DIALOG_ID,
        withAdditionalFooter: true,
      }
    );
  };

  const isVehiclePickerAvailable = !props.isVehicleChangeHidden && !props.isReadOnly;
  const isCalendarPickerAvailable =
    isVehiclePickerAvailable && props.isCalendarVehiclePicker !== true;
  const isCustomerPickerAvailable = isCreateMode && !props.isReadOnly;
  const isCustomerNameInputVisible = !isCustomerPickerAvailable || isNotNil(customerId);
  const isDeleteAvailable =
    isNotNilOrEmpty(testDrive?.id) &&
    isNotNil(props.businessCaseId) &&
    !props.isDeleteHidden &&
    !props.isReadOnly;

  const handleDeleteTestDrive = () => {
    if (isNil(props.businessCaseId)) {
      return;
    }
    const businessCaseId = props.businessCaseId;

    openConfirmDialog({
      onConfirm: async () =>
        await deleteTestDrive({
          testDriveId: testDrive?.id ?? '',
          businessCaseId,
        })
          .unwrap()
          .then(() => {
            closeDialog(TEST_DRIVE_DIALOG_ID);
          })
          .catch(handleApiError),
      text: i18n.t('entity.testDrive.labels.deleteTestDriveConfirmation'),
    });
  };

  return (
    <DataStatus
      isLoading={isTestDriveLoading || isPreselectedMileageLoading}
      isError={isTestDriveError}
      minHeight={60}
      data-testid={suffixTestId('dataStatus', {'data-testid': 'testDriveDialog'})}
    >
      <Form<TestDriveDialogSchemaType>
        experimentalZodSchema={isCreateMode ? TestDriveCreateDialogSchema : TestDriveDialogSchema}
        onSubmit={handleSubmit}
        defaultValues={defaultValues}
        isDisabled={props.isReadOnly}
      >
        {/* eslint-disable-next-line max-lines-per-function -- Pre-existing violation */}
        {(control, formApi) => (
          <>
            <Box overflowX="hidden">
              <Stack direction={['column', 'column', 'row']} spacing={4}>
                <VStack grow={2} spacing={4} maxWidth={['100%', '100%', '80%']}>
                  <Box>
                    <HStack width="100%" justify="space-between">
                      <Heading size={4}>
                        {i18n.t('entity.testDrive.labels.vehicleSectionHeader')}
                      </Heading>
                      <Show when={isVehiclePickerAvailable}>
                        <Button
                          title={i18n.t('entity.testDrive.labels.changeVehicle')}
                          size="small"
                          leftIcon="action/cached"
                          onClick={() => handleSelectVehicle(formApi)}
                          isDisabled={isSubmitting}
                          variant="link"
                        />
                      </Show>
                    </HStack>
                    <Box paddingVertical={4}>
                      {/*
                        A `testDriveId`-only caller resolves the vehicle in a SECOND request, so
                        the empty state would read "no vehicle selected" — the opposite of truth.
                      */}
                      {match({isVehicleFetching, hasVehicle: isNotNilOrEmpty(vehicleData)})
                        .with({isVehicleFetching: true}, () => <DataStatus isLoading />)
                        .with({hasVehicle: true}, () => (
                          <VehicleWidget
                            vehicle={vehicleData!}
                            data-testid={suffixTestId('vehicleWidget', {
                              'data-testid': 'testDriveDialog',
                            })}
                          />
                        ))
                        .otherwise(() => (
                          <EmptyStatus
                            variant="vehicle"
                            headline={i18n.t('entity.testDrive.labels.emptyVehicle')}
                            action={
                              isVehiclePickerAvailable
                                ? {
                                    title: i18n.t('entity.testDrive.labels.selectVehicle'),
                                    variant: 'primary',
                                    leftIcon: 'content/add_circle',
                                    onClick: () => handleSelectVehicle(formApi),
                                  }
                                : undefined
                            }
                          />
                        ))}
                      <Show when={formApi.formState.errors.vehicleId}>
                        <HelperText
                          errorMessage={formApi.formState.errors.vehicleId?.message}
                          data-testid="testDriveDialog-vehicleError"
                        />
                      </Show>
                      <Show when={isVehicleBlacklistedForTestDrive}>
                        <Box paddingTop={2}>
                          <Alert
                            variant="error"
                            type="inline"
                            message={i18n.t('entity.testDrive.labels.vehicleNotAvailableForSale')}
                            data-testid="testDriveDialog-vehicleNotAvailableForSale"
                          />
                        </Box>
                      </Show>
                    </Box>
                  </Box>

                  <VStack spacing={4}>
                    <HStack width="100%" justify="space-between">
                      <Heading size={4}>
                        {i18n.t('entity.testDrive.labels.handoverSectionHeader')}
                      </Heading>
                      <Show when={isCalendarPickerAvailable}>
                        <Button
                          title={i18n.t('entity.testDrive.labels.openCalendar')}
                          size="small"
                          leftIcon="action/today"
                          onClick={() => handleOpenCalendar(formApi)}
                          isDisabled={
                            isVehicleFetching || isNil(currentVehicleData) || isSubmitting
                          }
                          variant="link"
                        />
                      </Show>
                    </HStack>
                    <Grid columns={[1, 2, 4]} verticalSpacing={2}>
                      <FormField
                        control={control}
                        type="apiDate"
                        name="scheduledAtDate"
                        isRequired
                        label={i18n.t('entity.testDrive.labels.testDriveDate')}
                      />
                      <FormField
                        control={control}
                        type="time"
                        name="scheduledAtTime"
                        isRequired
                        label={i18n.t('entity.testDrive.labels.testDriveTime')}
                      />

                      <GridItem span={[1, 2]}>
                        <MileageInput
                          control={control}
                          formApi={formApi}
                          name="mileageBefore"
                          label={i18n.t('entity.testDrive.labels.mileageAtHandover')}
                          data-testid="testDriveDialog-mileageBefore"
                          vehicleId={selectedVehicleId}
                          shouldShowWarning={isNilOrZero(testDrive?.mileageBefore)}
                          isDisabled={props.isReadOnly}
                        />
                      </GridItem>

                      <GridItem span={[1, 2, 4, 2]}>
                        <Show when={isVehicleResolved}>
                          <HStack spacing={4} align="flex-end">
                            <Show when={hasFuel}>
                              <FormField
                                label={i18n.t('entity.testDrive.labels.fuelTank')}
                                name="fuelBefore"
                                type="fuelLevel"
                                control={control}
                                max={FUEL_TANK_RATIO_MAX}
                                min={FUEL_TANK_RATIO_MIN}
                                isDisabled={props.isReadOnly}
                                data-testid="testDriveDialog-fuelBefore"
                              />
                            </Show>
                            <Show when={hasBattery}>
                              <FormField
                                label={i18n.t('entity.vehicle.labels.batteryCharge')}
                                name="batteryChargeBefore"
                                type="batteryCharge"
                                control={control}
                                max={BATTERY_CHARGE_MAX}
                                min={BATTERY_CHARGE_MIN}
                                step={BATTERY_CHARGE_STEP}
                                isDisabled={props.isReadOnly}
                                data-testid="testDriveDialog-batteryChargeBefore"
                              />
                            </Show>
                          </HStack>
                        </Show>
                      </GridItem>
                      <GridItem>
                        <VStack spacing={1}>
                          <FormField
                            control={control}
                            type="choice"
                            name="userId"
                            label={i18n.t('entity.testDrive.labels.driver')}
                            options={usersOptions}
                            isLoading={areUsersLoading}
                          />
                          <DeletedUserAlertField
                            control={control}
                            name="userId"
                            users={usersWithDriver}
                            data-testid="testDriveDialog-driver"
                          />
                        </VStack>
                      </GridItem>
                      <VStack spacing={1}>
                        {isCustomerNameInputVisible ? (
                          <FormField
                            control={control}
                            type="text"
                            name="customerName"
                            label={i18n.t('entity.testDrive.labels.customerName')}
                            isRequired={isCustomerPickerAvailable}
                            labelActions={
                              isCustomerPickerAvailable
                                ? [
                                    {
                                      title: i18n.t('general.actions.change'),
                                      leftIcon: 'action/cached',
                                      onClick: () => handleSelectCustomer(formApi),
                                      isDisabled: isSubmitting,
                                    },
                                  ]
                                : undefined
                            }
                            data-testid="testDriveDialog-customerName"
                          />
                        ) : (
                          <VStack>
                            <Label isRequired={isCustomerPickerAvailable}>
                              {i18n.t('entity.testDrive.labels.customerName')}
                            </Label>
                            <Button
                              title={i18n.t('entity.testDrive.labels.selectCustomer')}
                              variant="secondary"
                              leftIcon="content/add"
                              onClick={() => handleSelectCustomer(formApi)}
                              isDisabled={isSubmitting}
                              isFullWidth
                              data-testid="testDriveDialog-selectCustomer"
                            />
                          </VStack>
                        )}
                        <Show when={formApi.formState.errors.customerId}>
                          <HelperText
                            errorMessage={formApi.formState.errors.customerId?.message}
                            data-testid="testDriveDialog-customerHelper"
                          />
                        </Show>
                      </VStack>
                      <FormField
                        control={control}
                        type="text"
                        name="customerDriversLicence"
                        label={i18n.t('entity.testDrive.labels.customerDriversLicence')}
                      />
                    </Grid>

                    <Separator spacing={0} />
                    <VStack>
                      <Box paddingBottom={4}>
                        <VStack spacing={1}>
                          <Heading size={4}>
                            {i18n.t('entity.testDrive.labels.returnSectionHeader')}
                          </Heading>
                          <Text size="xSmall" color="tertiary">
                            {i18n.t('entity.testDrive.labels.returnSectionSubtitle')}
                          </Text>
                        </VStack>
                      </Box>
                      <Grid columns={[1, 2, 4]} verticalSpacing={2}>
                        <FormField
                          control={control}
                          type="apiDate"
                          name="returnedAtDate"
                          minDate={parseDate(formApi.watch('scheduledAtDate')) ?? startOfToday()}
                          label={i18n.t('entity.testDrive.labels.returnDate')}
                          onChange={(value) => {
                            if (value) {
                              if (isReturnedAtInPast(value, formApi.watch('returnedAtTime'))) {
                                formApi.setValue('state', 'completed');
                              } else {
                                formApi.setValue('state', 'planned');
                              }
                            }
                          }}
                        />

                        <FormField
                          control={control}
                          type="time"
                          name="returnedAtTime"
                          minTime={
                            formApi.watch('returnedAtDate') === formApi.watch('scheduledAtDate')
                              ? (formApi.watch('scheduledAtTime') as TimeType)
                              : undefined
                          }
                          label={i18n.t('entity.testDrive.labels.returnTime')}
                          onChange={(value) => {
                            if (value) {
                              if (isReturnedAtInPast(formApi.watch('returnedAtDate'), value)) {
                                formApi.setValue('state', 'completed');
                              } else {
                                formApi.setValue('state', 'planned');
                              }
                            }
                          }}
                        />
                        <GridItem span={[1, 2]}>
                          <MileageInput
                            control={control}
                            formApi={formApi}
                            name="mileageAfter"
                            label={i18n.t('entity.testDrive.labels.mileageAtReturn')}
                            data-testid="testDriveDialog-mileageAfter"
                            vehicleId={selectedVehicleId}
                            shouldShowWarning={isNilOrZero(testDrive?.mileageAfter)}
                            shouldShowDelta={isNilOrZero(testDrive?.mileageAfter)}
                            isDisabled={props.isReadOnly}
                          />
                        </GridItem>

                        <GridItem span={[1, 2, 4, 2]}>
                          <Show when={isVehicleResolved}>
                            <HStack spacing={4} align="flex-end">
                              <Show when={hasFuel}>
                                <FormField
                                  label={i18n.t('entity.testDrive.labels.fuelTank')}
                                  name="fuelAfter"
                                  type="fuelLevel"
                                  control={control}
                                  max={FUEL_TANK_RATIO_MAX}
                                  min={FUEL_TANK_RATIO_MIN}
                                  isDisabled={props.isReadOnly}
                                  data-testid="testDriveDialog-fuelAfter"
                                />
                              </Show>
                              <Show when={hasBattery}>
                                <FormField
                                  label={i18n.t('entity.vehicle.labels.batteryCharge')}
                                  name="batteryChargeAfter"
                                  type="batteryCharge"
                                  control={control}
                                  max={BATTERY_CHARGE_MAX}
                                  min={BATTERY_CHARGE_MIN}
                                  step={BATTERY_CHARGE_STEP}
                                  isDisabled={props.isReadOnly}
                                  data-testid="testDriveDialog-batteryChargeAfter"
                                />
                              </Show>
                            </HStack>
                          </Show>
                        </GridItem>
                      </Grid>
                      <Separator />
                      <Box flex={1}>
                        <VStack spacing={1}>
                          <HStack justify="space-between" align="flex-end">
                            <Label>{i18n.t('entity.testDrive.labels.testDriveNote')}</Label>
                            <Show when={!props.isReadOnly}>
                              <PredefinedNotes
                                note={formApi.watch('note') ?? null}
                                onPrefill={(note) => formApi.setValue('note', note)}
                                resource="TEST_DRIVE"
                                context="test_drive_note"
                                isLinkVariant
                                data-testid={suffixTestId('noteNotes', {
                                  'data-testid': 'testDriveDialog',
                                })}
                              />
                            </Show>
                          </HStack>
                          <FormField name="note" minRows={1} control={control} type="textarea" />
                        </VStack>
                      </Box>
                    </VStack>
                  </VStack>
                </VStack>

                <Box
                  width={['100%', '100%', '20%']}
                  backgroundColor="palettes.neutral.20.100"
                  borderRadius="medium"
                  padding={4}
                >
                  <VStack spacing={4}>
                    <HStack>
                      <TestDriveStateSwitcher
                        name="state"
                        control={control}
                        isDisabled={props.isReadOnly}
                      />
                    </HStack>

                    <Show when={isNotNilOrEmpty(testDrive?.id)}>
                      <HStack width="100%" justify="space-between">
                        <Box flex={1}>
                          <Label>{i18n.t('entity.testDrive.labels.createdAt')}</Label>
                          <Text size="small">
                            {testDrive?.createdAt
                              ? formatTime('dateTimeShort', parseDate(testDrive.createdAt))
                              : ''}
                          </Text>
                        </Box>

                        <Box flex={1}>
                          <Label>{i18n.t('entity.testDrive.labels.createdBy')}</Label>
                          <Text size="small">{getNaturalPersonFullName(userData)}</Text>
                        </Box>
                      </HStack>
                    </Show>

                    <VStack spacing={1}>
                      <HStack justify="space-between" align="center">
                        <Label>{i18n.t('entity.testDrive.labels.internalNote')}</Label>
                        <Show when={!props.isReadOnly}>
                          <PredefinedNotes
                            note={formApi.watch('internalNote') ?? null}
                            onPrefill={(note) => formApi.setValue('internalNote', note)}
                            resource="TEST_DRIVE"
                            context="test_drive_internal_note"
                            isLinkVariant
                            data-testid={suffixTestId('internalNoteNotes', {
                              'data-testid': 'testDriveDialog',
                            })}
                          />
                        </Show>
                      </HStack>
                      <FormField
                        name="internalNote"
                        minRows={1}
                        control={control}
                        type="textarea"
                      />
                    </VStack>

                    <VStack spacing={1}>
                      <HStack justify="space-between" align="center">
                        <Label>{i18n.t('entity.testDrive.labels.customerFeedback')}</Label>
                        <Show when={!props.isReadOnly}>
                          <PredefinedNotes
                            note={formApi.watch('customerFeedback') ?? null}
                            onPrefill={(note) => formApi.setValue('customerFeedback', note)}
                            resource="TEST_DRIVE"
                            context="test_drive_customer_feedback"
                            isLinkVariant
                            data-testid={suffixTestId('customerFeedbackNotes', {
                              'data-testid': 'testDriveDialog',
                            })}
                          />
                        </Show>
                      </HStack>
                      <FormField
                        name="customerFeedback"
                        minRows={1}
                        control={control}
                        type="textarea"
                      />
                    </VStack>

                    <Show when={isDeleteAvailable}>
                      <Box flex={1}>
                        <Button
                          leftIcon="action/delete"
                          title={i18n.t('entity.testDrive.labels.delete')}
                          variant="dangerLink"
                          onClick={handleDeleteTestDrive}
                        />
                      </Box>
                    </Show>
                  </VStack>
                </Box>
              </Stack>
            </Box>
            <Space vertical={4} />
            <DialogFooter>
              <ButtonGroup align="right">
                <Button
                  variant="secondary"
                  onClick={closeCurrentDialog}
                  title={i18n.t(
                    props.isReadOnly ? 'general.actions.close' : 'general.actions.discard'
                  )}
                />
                <Show when={!props.isReadOnly}>
                  <FormButton
                    variant="primary"
                    isLoading={isSubmitting}
                    isDisabled={
                      !isVehicleResolved ||
                      isVehicleBlacklistedForTestDrive ||
                      (isCreateMode && (isSaleVehicleFetching || isVehicleMileageFetching)) ||
                      isSubmitting
                    }
                    type="submit"
                    control={control}
                    title={i18n.t('general.actions.save')}
                  />
                </Show>
              </ButtonGroup>
            </DialogFooter>
          </>
        )}
      </Form>
    </DataStatus>
  );
}
